import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Content, ContentFilters, ContentStats, PaginatedContent } from '@/types/content'
import { contentApi } from '@/api/content'
import apiClient from '@/api/client'
import { CONTENT } from '@/api/endpoints'

interface PaginationState {
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

interface ContentStoreState {
  content: Content[]
  stats: ContentStats | null
  loading: boolean
  error: string | null
  analyzing: boolean
  deleting: boolean
  pagination: PaginationState
}

export const useContentStore = defineStore('content', () => {
  const content = ref<Content[]>([])
  const stats = ref<ContentStats | null>({
    total: 0,
    analyzed: 0,
    unanalyzed: 0,
    by_source: {}
  })
  const loading = ref(false)
  const error = ref<string | null>(null)
  const analyzing = ref(false)
  const deleting = ref(false)
  const pagination = ref<PaginationState>({
    page: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0
  })

  async function fetchContent(filters?: ContentFilters) {
    try {
      loading.value = true
      error.value = null
      console.log('Fetching content with filters:', filters)
      const response = await contentApi.list(filters)
      console.log('API Response:', response)
  
      // Ensure content is always an array, even if response is invalid
      if (!response) {
        console.error('No response from API')
        content.value = []
        pagination.value = {
          page: 1,
          pageSize: 10,
          totalItems: 0,
          totalPages: 0
        }
        throw new Error("No response from API")
      }

      // Ensure items is an array
      let items = Array.isArray(response.items) ? response.items : []
      
      // Apply client-side filtering for high relevance only
      items = items.filter(item => {
        // Check relevance in multiple locations
        const relevance = item.activism_relevance || 
                         item.analysis?.aktivizam_relevantnost || 
                         item.analysis?.activism_relevance || 
                         'niska';
        console.log(`Item ${item.id} relevance: ${relevance}`);
        return relevance.toLowerCase() === 'visoka';
      });
      
      content.value = items
      console.log('Content set to (after filtering):', content.value)
  
      // Store the requested page size (from filters, not the expanded one)
      const requestedPageSize = filters?.page_size || Number(response.page_size) || 10;
      
      // Update pagination based on filtered items
      pagination.value = {
        page: Number(response.page) || 1,
        pageSize: requestedPageSize,
        totalItems: items.length, // Use filtered count
        totalPages: Math.ceil(items.length / requestedPageSize) // Recalculate based on filtered items
      }
      
      console.log('Pagination set to:', pagination.value)

      // Transform content items if needed
      content.value = content.value.map(item => {
        // Get the relevance value from the analysis data correctly
        let activismRelevance: string | null = null;
        
        // First try to get directly from the top-level field 
        if (item.activism_relevance) {
          activismRelevance = item.activism_relevance;
        } 
        // Then try from analysis object in a type-safe way
        else if (item.analysis) {
          // This field definitely exists in the ContentAnalysis type
          if (item.analysis.aktivizam_relevantnost) {
            activismRelevance = item.analysis.aktivizam_relevantnost;
          }
          // This field is optional in our extended type
          else if ('activism_relevance' in item.analysis && item.analysis.activism_relevance) {
            activismRelevance = item.analysis.activism_relevance;
          }
        }
        // Finally, check analysis_result with type safety
        else if (item.analysis_result) {
          if (item.analysis_result.aktivizam_relevantnost) {
            activismRelevance = item.analysis_result.aktivizam_relevantnost;
          }
          else if ('activism_relevance' in item.analysis_result && item.analysis_result.activism_relevance) {
            activismRelevance = item.analysis_result.activism_relevance;
          }
        }
        
        console.log(`Item ${item.id} relevance value:`, activismRelevance || 'niska');
        
        return {
          ...item,
          // Ensure required properties exist
          id: item.id || '',
          title: item.title || '',
          content: item.content || '',
          analyzed: Boolean(item.analyzed),
          themes: Array.isArray(item.themes) ? item.themes : [],
          sentiment: item.sentiment || null,
          activism_relevance: activismRelevance || 'niska',
          key_actors: item.key_actors || { individuals: [], organizations: [], institutions: [] }
        };
      })
      
      // If we have more items than requested page size (due to using a larger page size for relevance filtering),
      // slice the content to match the requested page size
      if (filters?.relevance && content.value.length > requestedPageSize) {
        console.log(`Slicing ${content.value.length} items down to requested page size: ${requestedPageSize}`);
        content.value = content.value.slice(0, requestedPageSize);
        
        // Adjust pagination to reflect the new item count
        pagination.value.totalItems = content.value.length;
        pagination.value.totalPages = Math.max(1, Math.ceil(content.value.length / requestedPageSize));
      }
      
    } catch (e: any) {
      console.error('Error fetching content:', e)
      error.value = e.message || 'Failed to fetch content'
      content.value = []  // Ensure content is empty array on error
    } finally {
      loading.value = false
    }
  }
  
  

  async function fetchStats() {
    try {
      const response = await contentApi.getStats()
      stats.value = response || {
        total: 0,
        analyzed: 0,
        unanalyzed: 0,
        by_source: {}
      }
    } catch (e: any) {
      console.error('Failed to fetch stats:', e)
      stats.value = {
        total: 0,
        analyzed: 0,
        unanalyzed: 0,
        by_source: {}
      }
    }
  }

  async function analyzeContent(id: string) {
    try {
      await contentApi.analyze(id)
    } catch (e: any) {
      throw new Error(e.message || 'Failed to analyze content')
    }
  }

  async function analyzeMultiple(ids: string[]) {
    try {
      await contentApi.analyzeMultiple(ids)
    } catch (e: any) {
      throw new Error(e.message || 'Failed to analyze multiple content')
    }
  }

  async function deleteMultiple(ids: string[]) {
    try {
      deleting.value = true
      await apiClient.delete(CONTENT.DELETE, { 
        params: { 
          ids: ids.join(',') 
        } 
      })
    } catch (e: any) {
      throw new Error(e.message || 'Failed to delete content')
    } finally {
      deleting.value = false
    }
  }

  const analyzedContent = computed(() => content.value?.filter(item => item.analyzed) || [])
  const unanalyzedContent = computed(() => content.value?.filter(item => !item.analyzed) || [])

  return {
    content,
    stats,
    loading,
    error,
    analyzing,
    deleting,
    pagination,
    fetchContent,
    fetchStats,
    analyzeContent,
    analyzeMultiple,
    deleteMultiple,
    analyzedContent,
    unanalyzedContent
  }
}) 